import {
  API_ROUTES,
  API_ROUTE_PREFIX_HTTPS,
  API_ROUTE_V2_PREFIX_HTTPS,
  ICheckCheckoutEligibilityRequestParams,
  IEditTravellersRequestParams,
  IGetNextPageRouteRequestParams,
  IGetSuggestionsInfoRequestParams,
  IPageRequestParams,
} from "@tm/api-utils";
import { CONTEXT, LOB, PARTNER, PLATFORM } from "@tm/isomorphic-utils";
import {
  getHTTPSRoutePrefixOnClientSide,
  getLobOnClientSide,
  getPartnerOnClientSide,
  getPathPartnerOnClientSide,
  setURLQueryParams,
} from "../utils";
import { getDeviceIdForClient } from "../utils/getDeviceId";
import { API_HTTPS } from "./config";

export const paymentCheckout = async (reqData: any): Promise<any> => {
  try {
    const { partner = getPartnerOnClientSide(), data, query } = reqData;
    const params = {
      context: CONTEXT.STANDALONE_V2,
      ins_new_flow: true,
      lob: getLobOnClientSide(partner),
      itineraryId: data?.query?.itineraryId,
    };
    const urlParams = setURLQueryParams(params);
    const dvid = getDeviceIdForClient();

    const apiRoutePrefix = getHTTPSRoutePrefixOnClientSide(partner);

    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.post(
      `${apiRoutePrefix}/${pathPartner}/${API_ROUTES.PAYMENT_CHECKOUT}${
        urlParams ? `?${urlParams}` : ""
      }`,
      data,
      {
        headers: {
          cmp: sessionStorage.getItem("cmp") || undefined,
          dvid: dvid || "",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error", { error });
    throw error;
  }
};

export const sendPolicyEmail = async (data: any): Promise<any> => {
  try {
    const { bookingId, partner } = data;
    const params = { bookingID: bookingId };
    const urlParams = setURLQueryParams(params);
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);
    const response = await API_HTTPS.post(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${API_ROUTES.TRIGGER_EMAIL}${
        urlParams ? `?${urlParams}` : ""
      }`,
      {
        headers: {
          cmp: sessionStorage.getItem("cmp"),
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error: any) {
    console.log("Error", { error: error.message });
    throw error;
  }
};

export const editInsuranceDetails = async (
  reqData: IEditTravellersRequestParams
): Promise<any> => {
  try {
    const {
      bookingID,
      partner = getPartnerOnClientSide(),
      data = [],
    } = reqData;

    if (!bookingID)
      throw "editInsuranceDetails: Booking id not present or invalid";

    const urlParams = setURLQueryParams({ bookingID });
    const dvid = getDeviceIdForClient();
    const pathPartner = getPathPartnerOnClientSide(partner);

    const response = await API_HTTPS.put(
      `${API_ROUTE_PREFIX_HTTPS}/${pathPartner}/${
        API_ROUTES.EDIT_INSURANCE_DETAILS
      }${urlParams ? `?${urlParams}` : ""}`,
      data,
      {
        headers: {
          cmp: sessionStorage.getItem("cmp") || undefined,
          dvid: dvid || "",
        },
      }
    );
    return response?.data;
  } catch (error) {
    console.error("Error", { error });
    throw error;
  }
};

export const getNextPageRouteDetails = async (
  reqData: IGetNextPageRouteRequestParams
) => {
  const {
    partner = PARTNER.MMT,
    query,
    myPartnerGroupInsuranceEnabled,
    ...data
  } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();

  let url;
  if (myPartnerGroupInsuranceEnabled || partner === PARTNER.MYBIZ) {
    url = `${API_ROUTE_V2_PREFIX_HTTPS}/mmt/${API_ROUTES.GET_NEXT_PAGE_ROUTE}?${urlParams}`;
  } else if (partner === PARTNER.MYPARTNER) {
    url = `${API_ROUTE_PREFIX_HTTPS}/${API_ROUTES.GET_NEXT_PAGE_ROUTE_MYPARTNER}?${urlParams}`;
  } else {
    url = `${API_ROUTE_PREFIX_HTTPS}/${partner}/${API_ROUTES.GET_NEXT_PAGE_ROUTE}?${urlParams}`;
  }

  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });

  if (myPartnerGroupInsuranceEnabled || partner === PARTNER.MYBIZ) {
    return {
      responseStatus: response.data?.responseStatus,
      nextPageRouteConfig: response.data?.data?.nextPageRouteConfig,
    };
  }
  return response.data;
};

export const hitRetailConsentMyPartner = async (
  reqData: IPageRequestParams
) => {
  const { partner = PARTNER.MYPARTNER, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${API_ROUTES.HIT_RETAIL_CONSENT}?${urlParams}`;
  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getOrdersMyPartner = async (reqData: IPageRequestParams) => {
  const { partner = PARTNER.MYPARTNER, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: getLobOnClientSide(partner),
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${API_ROUTES.GET_ORDERS_RETAIL_MYPARTNER}?${urlParams}`;
  const response = await API_HTTPS.get(url, {
    params: data,
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const checkCheckoutEligibility = async (
  reqData: ICheckCheckoutEligibilityRequestParams
) => {
  const { partner = PARTNER.MMT, query, ...data } = reqData;
  const dvid = getDeviceIdForClient();
  const params = {
    lob: LOB.STANDALONE,
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    isCountryCodesSupported: true.toString(),
    partner,
    ...query,
  };

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_PREFIX_HTTPS}/${partner}/${API_ROUTES.CHECK_CHECKOUT_ELIGIBILITY}?${urlParams}`;

  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};

export const getSuggestionsInfo = async (
  reqData: IGetSuggestionsInfoRequestParams
) => {
  const { query, ...data } = reqData;

  const params = {
    lob: LOB.STANDALONE_MYBIZ,
    platform: PLATFORM.WEB,
    context: CONTEXT.STANDALONE_V2,
    partner: PARTNER.MYBIZ,
    ...query,
  };
  const dvid = getDeviceIdForClient();

  const urlParams = new URLSearchParams(params).toString();
  const url = `${API_ROUTE_V2_PREFIX_HTTPS}/mmt/${API_ROUTES.GET_SUGGESTIONS_INFO}?${urlParams}`;

  const response = await API_HTTPS.post(url, data, {
    headers: { dvid: dvid || "" },
  });
  return response.data;
};
